if ( typeof ModalBox !== 'function' ) {

  // seconds before a dismissed popup shows again; once never repeats
  const MODAL_FREQUENCIES = { day: 86400, week: 604800, month: 2419200 };

  // text inside [data-content-ignore] is left out of the seen-check, so only an edit re-shows the popup
  const MODAL_VOLATILE = 'script, style, template, noscript, [data-content-ignore]';

  const modalContentKey = elm => {
    let text = '';
    const walker = document.createTreeWalker(elm, NodeFilter.SHOW_TEXT);
    while (walker.nextNode()) {
      const volatile = walker.currentNode.parentElement.closest(MODAL_VOLATILE);
      if (!volatile || !elm.contains(volatile)) {
        text += walker.currentNode.nodeValue;
      }
    }
    text = text.replace(/\s+/g, ' ').trim();
    let hash = 0x811c9dc5;
    for (let i = 0; i < text.length; i++) {
      hash ^= text.charCodeAt(i);
      hash = Math.imul(hash, 0x01000193);
    }
    return (hash >>> 0).toString(36);
  };

  class ModalBox extends HTMLElement {

    constructor() {

      window.inertElems = document.querySelectorAll('[data-js-inert]');

      super();
      
			this._prefix = window.KT_PREFIX || '';
      this.o = {
        ...{
          show: 10,
          showOnPageOffset: -1,
          frequency: "day",
          enabled: true,
          showOnce: true,
          closeByKey: true,
          disableScroll: true,
          enableClose: false,
          type: false,
          blockTabNavigation: false,
          openedModalBodyClass: `${this._prefix}modal-opened`,
        },
        ...JSON.parse(this.dataset.options),
      };

			if ( this.o.type == 'cookies' ) {
				
				this.o.enabled = false;

				const privacy = () => window.Shopify && window.Shopify.customerPrivacy;

				const setConsent = granted => {
					if ( privacy() && ! Shopify.designMode ) {
						privacy().setTrackingConsent({
							"analytics": granted,
							"marketing": granted,
							"preferences": granted
						}, () => {});
					}
				}

				// a visitor who already accepted or declined, here or in Shopify's own banner
				const consentGiven = () => {
					const consent = typeof privacy().currentVisitorConsent === 'function' ? privacy().currentVisitorConsent() || {} : {};
					return ['analytics', 'marketing', 'preferences'].some(purpose => consent[purpose] === 'yes' || consent[purpose] === 'no');
				}

				this.querySelector('[data-js-cookies-accept]').addEventListener('click', () => setConsent(true));
				this.querySelector('[data-js-cookies-decline]').addEventListener('click', () => setConsent(false));

				this._onConsentCollected = () => {
					if ( this.classList.contains(`${this._prefix}active`) ) {
						this.hide();
					}
				};
				document.addEventListener('visitorConsentCollected', this._onConsentCollected);

				if ( window.Shopify && typeof window.Shopify.loadFeatures === 'function' && ! Shopify.designMode ) {
					window.Shopify.loadFeatures(
						[
							{
								name: 'consent-tracking-api',
								version: '0.2',
							},
						],
						error => {
							if ( error || ! privacy() || typeof privacy().shouldShowBanner !== 'function' ) {
								return;
							}
							if ( privacy().shouldShowBanner() && ! consentGiven() ) {
								this.show();
							}
						}
					);
				}
			}

      this._scrollTriggered = false; 

      if ( this.o.enabled ) {
        this._modalKey = `modal-${document.location.hostname}-${this.id}`;
        this._modalStorage = !localStorage.getItem(this._modalKey)
          ? "empty"
          : JSON.parse(localStorage.getItem(this._modalKey));

        const contentElm = this.querySelector("[data-content]");
        const legacyText = contentElm ? contentElm.textContent : undefined;
        this._modalText = modalContentKey(contentElm || this);

        const seen =
          this._modalStorage != 'empty' &&
          (this._modalStorage['content'] === this._modalText ||
            (legacyText !== undefined && this._modalStorage['content'] === legacyText));

        const timeNow = new Date().getTime();
        const inBetween = Math.round(
          (timeNow - this._modalStorage['shown']) / 1000
        );
        const repeatAfter = MODAL_FREQUENCIES[this.o.frequency];

        const showModal = !seen || (repeatAfter !== undefined && inBetween > repeatAfter);

        if ( showModal ) {

          if ( this.o.type == 'exit_intent_popup' ) {

            let lastMouseY = 0;
            let topOffset = 20;
            let exitIntentTriggered = false;

            this._exitIntentHandler = e => {
              const currentMouseY = e.clientY;
              if (currentMouseY < lastMouseY && currentMouseY < topOffset && ! Shopify.designMode && !exitIntentTriggered) {
                exitIntentTriggered = true;
                this.show();
                document.removeEventListener('mousemove', this._exitIntentHandler);
              }
              lastMouseY = currentMouseY;
            };
            document.addEventListener('mousemove', this._exitIntentHandler);

          } else if ( this.o.showOnPageOffset > 0 ) {
            this._onScroll = () => {
              const scrollable = document.documentElement.scrollHeight - window.innerHeight;
              // A page that cannot scroll never fires scroll events: it counts as fully scrolled.
              const scrollPercent = scrollable > 0 ? (window.scrollY / scrollable) * 100 : 100;
              if (
                scrollPercent >= this.o.showOnPageOffset &&
                !this._scrollTriggered
              ) {
                this._scrollTriggered = true;
                setTimeout(() => {
                  this.show();
                }, parseInt(this.o.show * 1000));
                window.removeEventListener('scroll', this._onScroll);
              }
            };
            window.addEventListener('scroll', this._onScroll, {
              passive: true,
            });
            this._onScroll();

          } else {
            setTimeout(() => {
              this.show();
            }, parseInt(this.o.show * 1000));
          }

        }
        this.querySelectorAll('[data-js-close]').forEach(elm =>
          elm.addEventListener('click', () => {
            this.hide(this.o.showOnce);
          })
        );
      } else {
        this.querySelectorAll('[data-js-close]').forEach(elm =>
          elm.addEventListener('click', () => {
            this.hide(this.o.showOnce);
          })
        );
      }

      if ( this.o.enableClose == true ) {
        this.querySelectorAll('[data-js-close]').forEach(elm =>
          elm.addEventListener('click', () => {
            this.hide(this.o.showOnce);
          })
        );
      }

      if ( this.o.closeByKey ) {
        this._onKeydown = e => {
          if (e.key === 'Escape') {
            if (this.classList.contains(`${this._prefix}active`)) {
              this.hide(this.o.showOnce);
            }
          }
        };
        document.addEventListener('keydown', this._onKeydown);
      }

      // a close control that is not a native button or link (a role="button" span) needs Enter and Space
      this.querySelectorAll('[data-js-close][role="button"]').forEach(elm =>
        elm.addEventListener('keydown', e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            elm.click();
          }
        })
      );
    }

    disconnectedCallback() {
      if ( this._exitIntentHandler ) {
        document.removeEventListener('mousemove', this._exitIntentHandler);
      }
      if ( this._onScroll ) {
        window.removeEventListener('scroll', this._onScroll);
      }
      if ( this._onKeydown ) {
        document.removeEventListener('keydown', this._onKeydown);
      }
      if ( this._onConsentCollected ) {
        document.removeEventListener('visitorConsentCollected', this._onConsentCollected);
      }
    }

    show(customContent = false) {

      if (customContent && document.querySelector(customContent)) {
        const content = document.querySelector(customContent).innerHTML;
        const modalCommon = document.getElementById('modal-common');
        if ( modalCommon ) {
          modalCommon.innerHTML = content;
          if (this.o.enableClose == true) {
            modalCommon.querySelectorAll('[data-js-close]').forEach(elm =>
              elm.addEventListener('click', () => {
                this.hide(this.o.showOnce);
              })
            );
          }
        }
      }

      // remember where focus was so hide() can give it back
      this._previouslyFocused = window.lastFocusedElm || ( document.activeElement !== document.body ? document.activeElement : null );

      this.setAttribute('style', '');
      setTimeout(() => {
        this.classList.add(`${this._prefix}active`);
        if (this.o.disableScroll) {
          document.body.classList.add(this.o.openedModalBodyClass);
        }
        if (this.o.blockTabNavigation) {
          window.inertElems.forEach(elm =>
            elm.setAttribute('inert', '')
          );
        }
      }, 10);

      // focus moves into the dialog: the first-focus element, else the dialog itself (tabindex="-1")
      setTimeout(() => {
        if (this.classList.contains(`${this._prefix}active`)) {
          (this.querySelector('[data-js-first-focus]') || this).focus({ preventScroll: true });
        }
      }, 250);

    }

    hide(remember = false) {

      const wasActive = this.classList.contains(`${this._prefix}active`);
      this.classList.remove(`${this._prefix}active`);
      document.body.classList.remove(this.o.openedModalBodyClass);
      setTimeout(() => {
        this.style.display = 'none';
      }, 500);
      if (remember && wasActive && this._modalKey && !Shopify.designMode) {
        localStorage.setItem(
          this._modalKey,
          JSON.stringify({
            shown: new Date().getTime(),
            content: this._modalText,
          })
        );
      }

      window.inertElems.forEach(elm =>
        elm.removeAttribute('inert')
      );

      const returnTo = window.lastFocusedElm || this._previouslyFocused;
      window.lastFocusedElm = null;
      this._previouslyFocused = null;
      const focusInside = this.contains(document.activeElement) || document.activeElement === document.body;
      if (wasActive && returnTo && returnTo.isConnected && focusInside) {
        setTimeout(() => {
          returnTo.focus({ preventScroll: true });
        }, 100);
      }

    }
  }

  if ( typeof customElements.get('modal-box') == 'undefined' ) {

    // Clone modal elements and move to end of body IF append-top-body
    const originalElements = document.querySelectorAll('modal-box');
    originalElements.forEach((originalElement) => {
      const dataOptions = JSON.parse(originalElement.dataset.options);
      if (dataOptions.appendToBody === true) {
        document.body.appendChild(originalElement);
      }
    });

    customElements.define('modal-box', ModalBox);

  }

  // Shopify events

  document.addEventListener('shopify:section:select', e => {
    if (e.target.classList.contains('mount-popup')) {
      e.target.querySelector('modal-box').style.display = 'block';
      e.target.querySelector('modal-box').show();
    }
  });
  document.addEventListener('shopify:block:select', e => {
    if (e.target.hasAttribute('data-modal-box')) {
      e.target.style.display = 'block';
      e.target.show();
    }
  });
  document.addEventListener('shopify:block:deselect', e => {
    if (e.target.hasAttribute('data-modal-box')) {
      e.target.hide();
    }
  });
  document.addEventListener('shopify:section:deselect', e => {
    if (e.target.classList.contains('mount-popup')) {
      e.target.querySelector('modal-box').hide();
    }
  });
	
}